import { ContactSupport } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import { Button } from "@mui/material";
import { PaymentMethod } from "@stripe/stripe-js";
import cx from "classnames";
import React, { useCallback, useContext, useEffect, useState } from "react";

import { ReactComponent as CrownIcon } from "assets/crown.svg";
import DotElastic from "components/DotElastic";
import ManagePaymentMethodsModal from "components/Payment/UpgradeFlowComponents/ManagePaymentMethodsModal";
import { SLAPSHOT_SUPPORT_URL } from "constants/env";
import { UserContext } from "layouts/SlapshotMainLayout";
import { InvoiceCompact } from "models/Payment";
import { Subscription } from "models/User";
import { PLANS_INDEX } from "pages/UpgradePlanFlow/UpgradePlanFlow";
import { getInvoices, getPaymentMethods } from "services/SubscriptionService";
import {
  formatDate,
  formatPrice,
  formatSize,
  formatTimestamp,
} from "utils/helper";

import styles from "./BillingTab.module.scss";

export const estimateInvoiceTotal = (subscription: Subscription | null) => {
  if (!subscription) return 0;
  return (
    subscription.renewal_plan.price.unit_amount_decimal +
    subscription.plan.extra_frame_cost.unit_amount_decimal *
      Math.max(0, subscription.frames_used - subscription.frames_limit) +
    subscription.plan.autopilot_frame_cost.unit_amount_decimal *
      Math.max(
        0,
        subscription.autopilot_frames_used -
          subscription.autopilot_frames_limit,
      )
  );
};

interface BillingTabProps {
  subscription: Subscription;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const BillingTab: React.FC<BillingTabProps> = ({ setStep, subscription }) => {
  const [defaultMethod, setDefaultMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[] | null>(
    null,
  );
  const [invoices, setInvoices] = useState<InvoiceCompact[] | null>(null);
  const [showManageMethodsModal, setShowManageMethodsModal] = useState(false);

  const fetchPaymentMethods = useCallback(() => {
    setPaymentMethods(null);
    getPaymentMethods(subscription.id).then((data) => {
      setPaymentMethods(data.payment_methods);
      if (data.default_payment_method) {
        setDefaultMethod(data.default_payment_method);
      }
    });
  }, [subscription.id]);

  const fetchInvoices = useCallback(() => {
    getInvoices(subscription.id).then((data: InvoiceCompact[]) => {
      setInvoices(data.filter((invoice) => !invoice.metadata?.is_autopilot));
    });
  }, [subscription.id]);

  useEffect(() => {
    fetchPaymentMethods();
    fetchInvoices();
  }, [fetchPaymentMethods, fetchInvoices, subscription]);

  const getButtonLabel = () => {
    if (subscription.plan.name === "Studio") {
      return "Manage";
    }
    if (subscription.renewal_date) {
      return "Upgrade";
    }
    return "Renew";
  };

  return (
    <div className={styles.container}>
      {subscription && (
        <>
          <div className={styles.tableCon}>
            <table border={1} className={styles.table}>
              <tr>
                <td>
                  <div>
                    <div>Next invoice issue date</div>
                    <div className={styles.tdValue}>
                      {subscription.renewal_date &&
                      estimateInvoiceTotal(subscription) > 0
                        ? formatDate(subscription.renewal_date)
                        : "--"}
                    </div>
                  </div>
                </td>

                <td>
                  <div>
                    <div>Monthly no. of seats</div>
                    <div className={styles.tdValue}>Unlimited</div>
                  </div>
                </td>

                <td>
                  <div>
                    <div>Usage</div>
                    <div className={styles.usage}>
                      <div>
                        <div style={{ fontSize: "10px", marginBottom: "4px" }}>
                          Assets storage:
                        </div>
                        <div>
                          <span className={styles.storageUsed}>
                            {formatSize(subscription.storage_used)}
                          </span>
                          <span>
                            {` / ${formatSize(subscription.plan.storage, "GB")}`}
                          </span>
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: "10px", marginBottom: "4px" }}>
                          Frames exported:
                        </div>
                        <div>
                          <span className={styles.storageUsed}>
                            {subscription.frames_used}
                          </span>
                          <span>{` / ${subscription.frames_limit}`}</span>
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: "10px", marginBottom: "4px" }}>
                          Autopilot frames exported:
                        </div>
                        <div>
                          <span className={styles.storageUsed}>
                            {subscription.autopilot_frames_used}
                          </span>
                          <span>{` / ${subscription.autopilot_frames_limit}`}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

              <tr>
                <td>
                  <div>
                    <div>
                      {subscription.renewal_date
                        ? "Currently active plan"
                        : "Expired plan"}
                    </div>
                    <div className={styles.currentPlan}>
                      {`${subscription.plan.name} Plan`}
                      {subscription.renewal_plan.price.unit_amount_decimal <
                        subscription.plan.price.unit_amount_decimal &&
                        subscription.renewal_date && (
                          <span
                            className={styles.upcomingPlan}
                          >{`(${subscription.renewal_plan.name} Plan from ${formatDate(subscription.renewal_date)})`}</span>
                        )}
                    </div>
                    <div
                      className={cx(
                        styles.upgradeBtncon,
                        subscription.is_processing_payment &&
                          styles.upgradeBtnconDisabled,
                      )}
                    >
                      <Button
                        disabled={subscription.is_processing_payment}
                        className={styles.upgrade}
                        onClick={() => setStep(PLANS_INDEX)}
                      >
                        <CrownIcon className={styles.crownIcon} />
                        {getButtonLabel()}
                      </Button>
                    </div>
                  </div>
                </td>

                <td>
                  <div>
                    <div>Estimated invoice total</div>
                    <div className={styles.estimate}>
                      {estimateInvoiceTotal(subscription) > 0
                        ? `${formatPrice({ currency: subscription.plan.price.currency, unit_amount_decimal: estimateInvoiceTotal(subscription) })}`
                        : "--"}
                    </div>
                  </div>
                </td>

                <td>
                  <div>
                    <div>Payment method</div>

                    {defaultMethod && (
                      <div className={styles.defaultMethod}>
                        <div className={styles.tdValue}>
                          {defaultMethod.card
                            ? `**** **** **** ${defaultMethod.card?.last4}`
                            : defaultMethod.type}
                        </div>
                        {defaultMethod.card && (
                          <div
                            style={{ fontSize: "10px", color: "#FFFFFF99" }}
                          >{`Expires ${defaultMethod.card?.exp_month}/${defaultMethod.card?.exp_year}`}</div>
                        )}
                      </div>
                    )}

                    <div
                      className={styles.manageMethods}
                      onClick={() => setShowManageMethodsModal(true)}
                    >
                      Manage Payment methods
                    </div>
                  </div>
                </td>
              </tr>
            </table>
          </div>

          {!!invoices?.length && (
            <div className={styles.invoicesCon}>
              <div className={styles.invoiceHeader}>
                <div className={styles.invoicesHeading}>Invoices</div>
                <Button
                  className={cx(
                    styles.slapShotButtonOutline,
                    styles.contactSupport,
                  )}
                  onClick={() => {
                    window.open(SLAPSHOT_SUPPORT_URL, "_blank");
                  }}
                >
                  <ContactSupport style={{ marginRight: "5px" }} />
                  Contact Support
                </Button>
              </div>

              <div className={styles.invTableCon}>
                <table border={1} className={styles.invTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Plan</th>
                      <th>No. of Seats</th>
                      <th>Assets Storage</th>
                      <th>Exported Frames</th>
                      <th>Autopilot Frames</th>
                      <th>Invoice Total</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>

                  <tbody>
                    {invoices.map((invoice) => {
                      return (
                        <tr key={invoice.id}>
                          <td>
                            {invoice.date &&
                              formatTimestamp(invoice.date, {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                          </td>

                          <td>
                            {invoice?.metadata?.plan_name &&
                              `${invoice?.metadata?.plan_name} Plan`}
                          </td>

                          <td>Unlimited</td>

                          <td>
                            {invoice?.metadata?.storage_used &&
                              formatSize(
                                +(invoice?.metadata?.storage_used || 0),
                              )}
                          </td>

                          <td>{invoice?.metadata?.frames_used || 0}</td>

                          <td>
                            {invoice?.metadata?.autopilot_frames_used || 0}
                          </td>

                          <td>{formatPrice(invoice.price)}</td>

                          <td>
                            {invoice.receipt_url && (
                              <div
                                className={styles.downloadInvoice}
                                onClick={() => {
                                  if (invoice.receipt_url)
                                    window.open(invoice.receipt_url, "_blank");
                                }}
                              >
                                <DownloadIcon />
                                Download
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {invoices === null && (
            <div className={styles.loader}>
              <DotElastic />
            </div>
          )}
        </>
      )}

      {showManageMethodsModal && (
        <ManagePaymentMethodsModal
          onBack={() => setShowManageMethodsModal(false)}
          paymentMethods={paymentMethods}
          defaultMethod={defaultMethod}
          fetchPaymentMethods={fetchPaymentMethods}
          subscription={subscription}
        />
      )}
    </div>
  );
};

export default BillingTab;
